import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
// src/pages/fixed-assets/FixedAssetDetail.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/solid";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "../../components/Button";
import { AppleComboBox } from "../../components/AppleComboBox";
import { StatusBubble } from "../../components/StatusBubble";
import { apiDelete, apiGet, apiPatch, apiPost } from "../../api/client";
import { companyRoute } from "../../utils/routeHelpers";
import { formatNumber, formatMoney } from "../../utils/formatNumber";
import {
  mdyToYmd,
  ymdTodayLocal,
  formatMMDDYYYYFromYMD,
  normalizeMDY,
} from "../../utils/dateUtils";
import { A } from "../../styles/appleClasses";
import { AppleNumberInput } from "../../components/AppleNumberInput";
import MemoSection from "../../components/MemoSection";
import {
  isClosedPeriodError,
} from "../../utils/closedPeriod";
import {
  randomUuidKey,
  canonicalFixedAssetPayment,
  fingerprintFixedAssetPayment,
  decideFixedAssetPaymentAttempt,
  resolveFixedAssetPaymentSuccess,
  resolveFixedAssetPaymentDefiniteRejection,
  resolveFixedAssetPaymentAmbiguousFailure,
  shouldRestoreAmbiguousAttempt,
  classifyFixedAssetPaymentFailure,
  IDLE_FIXED_ASSET_PAYMENT_ATTEMPT,
  type FixedAssetPaymentAttempt,
} from "../../utils/fixedAssetPaymentIdempotency";

type FixedAssetRow = {
  id: number;
  name: string;
  asset_account_id?: number | null;
  asset_account_name?: string | null;
  disposed?: boolean;
  memo?: string | null;

  purchase_amount?: number | null;
  on_account?: boolean;
  due_date?: string | null;
  amount_paid?: number | null;
  amount_due?: number | null;
  payment_status?: string | null;

  payable_account_id?: number | null;
  payable_account_name?: string | null;
  purchase_bank_account_id?: number | null;
  purchase_bank_account_name?: string | null;
};

type ScheduleSummaryRow = {
  id: number;
  asset_id: number;
  asset_name: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  cost: number;
  accum_depr: number;
  nbv: number;
  status: "active" | "closed" | "voided" | string;
};

type PostingRow = {
  id: number;
  period_end: string; // YYYY-MM-DD
  cost: number;
  amount: number;
  nbv: number;
  journal_entry_id?: number | null;
};

type Account = {
  id: number | string;
  name: string;
  fallbackDisplay?: boolean;
  kind?: string | null;
  type?: string | null;
  category?: string | null;
  account_type?: string | null;
  group?: string | null;
  is_system?: boolean;
};

const lower = (s?: string | null) =>
  String(s || "")
    .toLowerCase()
    .trim();

const isSystemAccount = (a: any) => {
  if (a?.is_system) return true;

  const k =
    lower(a?.kind) ||
    lower(a?.type) ||
    lower(a?.category) ||
    lower(a?.account_type) ||
    lower(a?.group);

  if (k.includes("system") || k.endsWith("_system")) return true;

  const n = lower(a?.name);
  return n.includes("system");
};

const isCashBankAccount = (a: any) => {
  const k =
    lower(a?.kind) ||
    lower(a?.type) ||
    lower(a?.category) ||
    lower(a?.account_type) ||
    lower(a?.group);

  if (k.includes("cashbank") || k.includes("cash") || k.includes("bank"))
    return true;

  const n = lower(a?.name);
  return (
    n.includes("cash") ||
    n.includes("bank") ||
    n.includes("checking") ||
    n.includes("cheque") ||
    n.includes("savings")
  );
};

function money(n: number | null | undefined) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return formatMoney(n);
}

function titleCaseStatus(s: string) {
  const low = lower(s || "");
  if (low.includes("active")) return "Active";
  if (low.includes("disposed")) return "Disposed";
  if (low.includes("closed")) return "Closed";
  if (low.includes("void")) return "Voided";
  if (low.includes("no schedule")) return "No Schedule";
  return s || "Active";
}

// Canonical pill style (ring-1 + soft tint)
function statusPillClasses(label: string) {
  const low = lower(label);
  const base =
    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium leading-none ring-1";

  if (low.includes("active"))
    return `${base} bg-appleblue/10 text-appleblue ring-appleblue/20`;

  if (low.includes("closed"))
    return `${base} bg-gray-100 text-gray-700 ring-gray-200`;

  if (low.includes("disposed"))
    return `${base} bg-gray-200/70 text-gray-600 ring-gray-300`;

  if (low.includes("no schedule"))
    return `${base} bg-amber-50 text-amber-700 ring-amber-200`;

  if (low.includes("void"))
    return `${base} bg-gray-100 text-gray-600 ring-gray-200`;

  return `${base} bg-gray-100 text-gray-600 ring-gray-200`;
}

function getPaymentBubbleProps(label: string): {
  label: string;
  bubbleType: "paid" | "partial" | "unpaid" | "open";
} {
  const low = lower(label);

  if (low === "paid") return { label: "Paid", bubbleType: "paid" };
  if (low === "partial") return { label: "Partial", bubbleType: "partial" };
  if (low === "unpaid") return { label: "Unpaid", bubbleType: "unpaid" };

  return { label: label || "Open", bubbleType: "open" };
}

function ModalShell({
  open,
  title,
  onClose,
  children,
  cardCls,
  iconBtnCls,
  bodyCls,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  cardCls: string;
  iconBtnCls: string;
  bodyCls: string;
}) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 ${bodyCls}`}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={`relative w-full max-w-xl ${cardCls} p-5`}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[15px] font-semibold text-gray-700">{title}</div>
          <button
            className={iconBtnCls}
            onClick={onClose}
            aria-label={t("directory.common.close")}
            type="button"
            title={t("directory.common.close")}
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {children}
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  open,
  title,
  message,
  confirmText,
  deleting,
  error,
  onCancel,
  onConfirm,
  cardCls,
  bodyCls,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  deleting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
  cardCls: string;
  bodyCls: string;
}) {
  const { t } = useTranslation();
  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 ${bodyCls}`}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className={`relative w-full max-w-md ${cardCls} p-5`}>
        <div className="text-[15px] font-semibold text-gray-700 mb-2">
          {title}
        </div>
        <div className="text-[13px] font-normal text-gray-700 whitespace-pre-line">
          {message}
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-[13px] text-red-700 whitespace-pre-wrap">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={deleting}
          >
            {t("directory.common.cancel")}
          </Button>

          <Button
            variant="primary"
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 active:bg-red-800 focus:ring-red-200"
          >
            {deleting ? t("directory.common.deleting") : confirmText ?? t("directory.common.delete")}
          </Button>
        </div>
      </div>
    </div>
  );
}

type DisplayMessage = { key: ParseKeys } | { raw: string };
const localMessage = (key: ParseKeys): DisplayMessage => ({ key });
const errorMessage = (error: any, key: ParseKeys): DisplayMessage => {
  const raw = error?.response?.data?.detail || error?.message;
  return raw ? { raw } : localMessage(key);
};

export default function FixedAssetDetail() {
  const { t } = useTranslation();
  const displayMessage = (message: DisplayMessage | null) => {
    if (message === null) return null;
    if ("raw" in message) return message.raw;
    if (message.key === "sales.errors.closedSubmit") {
      return t("sales.errors.closedSubmit", { date: t("sales.errors.closedDate") });
    }
    return t(message.key);
  };
  // Canonical English labels continue to drive all predicates and styles.
  const displayAssetStatus = (label: string) => {
    switch (label) {
      case "Active": return t("spendingFixedAssets.status.active");
      case "Closed": return t("spendingFixedAssets.status.closed");
      case "No Schedule": return t("spendingFixedAssets.status.noSchedule");
      case "Disposed": return t("spendingFixedAssets.status.disposed");
      case "Voided": return t("sales.list.voided");
      default: return label;
    }
  };
  const displayPaymentStatus = (label: string) => {
    switch (label) {
      case "Paid": return t("sales.list.paid");
      case "Partial": return t("sales.list.partial");
      case "Unpaid": return t("sales.list.unpaid");
      default: return label;
    }
  };
  const paymentAccountLabel = (account: Account) => account.fallbackDisplay
    ? t("fixedAssetDetail.accountFallback", { id: account.id })
    : account.name || "";
  const navigate = useNavigate();
  const { company_id = "", asset_id = "" } = useParams<{
    company_id: string;
    asset_id: string;
  }>();

  // appleClasses (safe fallbacks)
  const bodyCls = (A as any).body || "text-[13px] font-normal text-gray-700";
  const cardCls =
    (A as any).card || "bg-white border border-gray-200 rounded-2xl shadow-sm";
  const tableCls = (A as any).table || "w-full text-[13px] text-gray-700";
  const iconBtnCls =
    (A as any).iconBtn ||
    "h-10 w-10 inline-flex items-center justify-center rounded-xl hover:bg-gray-100 active:bg-gray-200 " +
      "focus:outline-none focus:ring-2 focus:ring-appleblueD disabled:opacity-50 disabled:cursor-not-allowed";
  const inputCls =
    (A as any).input ||
    "w-full h-10 rounded-xl px-4 border border-gray-200 bg-white text-[13px] font-normal text-gray-700 " +
      "focus:outline-none focus:ring-2 focus:ring-appleblueD focus:border-appleblue transition";
  const labelCls = "block text-xs text-gray-500 mb-1";

  const withCompany = (path: string) => {
    const sep = path.includes("?") ? "&" : "?";
    return `${path}${sep}company_id=${company_id}`;
  };

  const getC = async <T,>(path: string): Promise<T> =>
    apiGet<T>(withCompany(path));

  const [asset, setAsset] = useState<FixedAssetRow | null>(null);
  const [schedSummary, setSchedSummary] = useState<ScheduleSummaryRow | null>(
    null,
  );

  const [postings, setPostings] = useState<PostingRow[]>([]);
  const [historySupported, setHistorySupported] = useState(true);

  const [cashAccounts, setCashAccounts] = useState<Account[]>([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<DisplayMessage | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);

  // Memo
  const [memo, setMemo] = useState<string>("");
  const [editingMemo, setEditingMemo] = useState(false);
  const [memoDraft, setMemoDraft] = useState("");
  const [memoSaving, setMemoSaving] = useState(false);
  const [memoError, setMemoError] = useState<DisplayMessage | null>(null);

  // Delete popup
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<DisplayMessage | null>(null);

  // Dispose modal
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [disposeDateMdy, setDisposeDateMdy] = useState(() =>
    formatMMDDYYYYFromYMD(ymdTodayLocal()),
  );
  const [disposeProceeds, setDisposeProceeds] = useState<string>("");
  const [disposeBank, setDisposeBank] = useState<Account | null>(null);
  const [disposeMemo, setDisposeMemo] = useState<string>("");
  const [disposeErr, setDisposeErr] = useState<DisplayMessage | null>(null);
  const [disposing, setDisposing] = useState(false);

  const backToList = () =>
    navigate(companyRoute(company_id, "/recognition/fixed-assets"));

  const topStartYmd = useMemo(
    () => schedSummary?.start_date || null,
    [schedSummary?.start_date],
  );
  const topEndYmd = useMemo(
    () => schedSummary?.end_date || null,
    [schedSummary?.end_date],
  );

  const topCost = useMemo(() => {
    const n = schedSummary?.cost as number | undefined;
    return Number.isFinite(Number(n)) ? Number(n) : null;
  }, [schedSummary?.cost]);

  const topNbv = useMemo(() => {
    const n = schedSummary?.nbv as number | undefined;
    return Number.isFinite(Number(n)) ? Number(n) : null;
  }, [schedSummary?.nbv]);

  const purchaseAmount = useMemo(() => {
    const n = asset?.purchase_amount;
    return typeof n === "number" && Number.isFinite(n) ? n : topCost;
  }, [asset?.purchase_amount, topCost]);

  const amountPaid = useMemo(() => {
    const n = asset?.amount_paid;
    return typeof n === "number" && Number.isFinite(n) ? n : 0;
  }, [asset?.amount_paid]);

  const amountDue = useMemo(() => {
    const n = asset?.amount_due;
    return typeof n === "number" && Number.isFinite(n) ? n : 0;
  }, [asset?.amount_due]);

  const paymentStatusLabel = useMemo(() => {
    const s = lower(asset?.payment_status || "");

    if (s === "paid") return "Paid";
    if (s === "partial") return "Partial";
    if (s === "unpaid") return "Unpaid";

    if (asset?.on_account) {
      if (amountDue <= 0 && (purchaseAmount || 0) > 0) return "Paid";
      if (amountPaid > 0) return "Partial";
      if (amountDue > 0) return "Unpaid";
    }

    return "—";
  }, [
    asset?.payment_status,
    asset?.on_account,
    amountDue,
    amountPaid,
    purchaseAmount,
  ]);

  const paymentBubble = useMemo(() => {
    return getPaymentBubbleProps(paymentStatusLabel);
  }, [paymentStatusLabel]);

  const showPaymentSection = useMemo(() => {
    return !!asset?.on_account || amountPaid > 0 || amountDue > 0;
  }, [asset?.on_account, amountPaid, amountDue]);

  const canRecordPayment = useMemo(() => {
    return amountDue > 0;
  }, [amountDue]);

  const statusLabel = useMemo(() => {
    if (asset?.disposed) return "Disposed";
    if (!schedSummary) return "No Schedule";
    return titleCaseStatus(schedSummary.status || "No Schedule");
  }, [asset?.disposed, schedSummary]);

  const deleteAvailable = useMemo(() => {
    if (!asset) return false;
    if (asset.disposed) return false;
    if (postings.length > 0) return false;
    return true;
  }, [asset, postings.length]);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentDateMdy, setPaymentDateMdy] = useState(() =>
    formatMMDDYYYYFromYMD(ymdTodayLocal()),
  );
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [paymentBank, setPaymentBank] = useState<Account | null>(null);
  const [paymentMemo, setPaymentMemo] = useState<string>("");
  const [paymentErr, setPaymentErr] = useState<DisplayMessage | null>(null);
  const [paying, setPaying] = useState(false);

  // Fixed Asset Payment idempotency attempt lifecycle (see
  // utils/fixedAssetPaymentIdempotency.ts). A ref, not state: must be
  // stable across renders, must not itself trigger a re-render, and must
  // be readable synchronously by the in-flight guard below. Scoped to a
  // single FixedAssetPaymentAttempt (state + assetId + key + fingerprint
  // + submitted payload together) rather than independent refs, so the
  // lifecycle can never observe an invalid partial combination (e.g. a
  // key set with no matching fingerprint). Persists across dialog
  // close/reopen AND across navigating between different assets on this
  // same mounted route component -- see decideFixedAssetPaymentAttempt's
  // cross-asset handling and openPayment below.
  const fixedAssetPaymentAttemptRef = useRef<FixedAssetPaymentAttempt>(
    IDLE_FIXED_ASSET_PAYMENT_ATTEMPT,
  );
  // Purely synchronous double-click/repeat-tap guard -- independent of
  // the attempt state machine above. Mirrors PurchaseDetail.tsx's
  // vendorPaymentInFlightRef / CreditCardPayableDetail.tsx's
  // paymentInFlightRef.
  const fixedAssetPaymentInFlightRef = useRef(false);

  // Close menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest("[data-menu-root='fa-detail']")) return;
      setMenuOpen(false);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [menuOpen]);

  async function refresh() {
    if (!asset_id || !company_id) return;
    setLoading(true);
    setErr(null);

    try {
      const found = await getC<FixedAssetRow>(`/fixed-assets/${asset_id}`);
      setAsset(found);

      const loadedMemo = (found?.memo ?? "") as string;
      setMemo(loadedMemo);
      setMemoDraft(loadedMemo);
      setEditingMemo(false);
      setMemoError(null);

      const summaries = await getC<ScheduleSummaryRow[]>(
        `/depreciations?asset_id=${asset_id}`,
      );
      const s0 = (summaries || [])[0] || null;
      setSchedSummary(s0);

      if (s0?.id) {
        try {
          const rows = await getC<PostingRow[]>(
            `/depreciations/${s0.id}/postings`,
          );
          setPostings(rows || []);
          setHistorySupported(true);
        } catch {
          setPostings([]);
          setHistorySupported(false);
        }
      } else {
        setPostings([]);
        setHistorySupported(true);
      }

      // Cash/Bank accounts (UI-enforced filter)
      let accs: any[] = [];
      try {
        accs = await getC<any[]>("/accounts?kind=cashbank");
      } catch {
        accs = [];
      }

      if (!Array.isArray(accs) || accs.length === 0) {
        try {
          accs = await getC<any[]>("/accounts");
        } catch {
          accs = [];
        }
      }

      const cashBankOnly: Account[] = (accs || [])
        .filter(isCashBankAccount)
        .filter((a) => !isSystemAccount(a))
        .map((x) => ({ id: x.id, name: x.name }));

      setCashAccounts(cashBankOnly);

      if (
        paymentBank?.id &&
        !cashBankOnly.some((a) => String(a.id) === String(paymentBank.id))
      ) {
        setPaymentBank(null);
      }
      if (
        disposeBank?.id &&
        !cashBankOnly.some((a) => String(a.id) === String(disposeBank.id))
      ) {
        setDisposeBank(null);
      }
    } catch (e: any) {
      setErr(errorMessage(e, "fixedAssetDetail.loadError"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!company_id || !asset_id) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company_id, asset_id]);

  const goEdit = () =>
    navigate(companyRoute(company_id, `/fixed-assets/${asset_id}/edit`));

  const openDispose = () => {
    setMenuOpen(false);
    setDisposeErr(null);
    setDisposeDateMdy(formatMMDDYYYYFromYMD(ymdTodayLocal()));
    setDisposeProceeds("");
    setDisposeBank(null);
    setDisposeMemo("");
    setDisposeOpen(true);
  };

  const saveDispose = async () => {
    if (!asset?.id) return;
    setDisposeErr(null);

    const ymd = mdyToYmd(disposeDateMdy);
    if (!ymd) return setDisposeErr(localMessage("fixedAssetDetail.invalidDate"));

    const proceeds = Number(
      String(disposeProceeds || "0")
        .replace(/,/g, "")
        .trim(),
    );
    if (!Number.isFinite(proceeds) || proceeds < 0) {
      return setDisposeErr(localMessage("fixedAssetDetail.nonnegativeProceeds"));
    }

    if (proceeds > 0 && !disposeBank?.id) {
      return setDisposeErr(localMessage("fixedAssetDetail.selectProceedsAccount"));
    }

    setDisposing(true);
    try {
      await apiPost(withCompany(`/fixed-assets/${Number(asset_id)}/dispose`), {
        date: ymd,
        proceeds,
        bank_account_id: proceeds > 0 ? Number(disposeBank!.id) : null,
        memo: disposeMemo?.trim() || undefined,
      });

      setDisposeOpen(false);
      await refresh();
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setDisposeErr(localMessage("sales.errors.closedSubmit"));
        return;
      }

      setDisposeErr(errorMessage(e, "fixedAssetDetail.disposeError"));
    } finally {
      setDisposing(false);
    }
  };

  useEffect(() => {
    const p = Number(
      String(disposeProceeds || "0")
        .replace(/,/g, "")
        .trim(),
    );
    if (!Number.isFinite(p) || p <= 0) setDisposeBank(null);
  }, [disposeProceeds]);

  const openPayment = () => {
    setPaymentErr(null);

    const attempt = fixedAssetPaymentAttemptRef.current;
    const currentAssetId = asset?.id != null ? Number(asset.id) : null;
    const restore =
      currentAssetId != null &&
      shouldRestoreAmbiguousAttempt(attempt, currentAssetId) &&
      attempt.submittedPayload;

    if (restore) {
      // An ambiguous attempt for THIS asset is still unresolved -- restore
      // the exact values that were submitted, rather than resetting to
      // fresh defaults. Resetting here would almost always produce a
      // fingerprint mismatch on the very next submit (today's date and
      // the current outstanding balance rarely coincide with what was
      // actually sent), trapping the user behind the edited-retry block
      // with no visible way to make a safe, unchanged retry.
      const payload = attempt.submittedPayload!;
      setPaymentDateMdy(formatMMDDYYYYFromYMD(payload.date));
      setPaymentAmount(payload.amount);
      const matchedAccount =
        cashAccounts.find(
          (a) => String(a.id) === String(payload.payment_account_id),
        ) || {
          id: payload.payment_account_id,
          name: `Account #${payload.payment_account_id}`,
          fallbackDisplay: true,
        };
      setPaymentBank(matchedAccount);
      setPaymentMemo(payload.memo || "");
    } else {
      setPaymentDateMdy(formatMMDDYYYYFromYMD(ymdTodayLocal()));
      setPaymentAmount(amountDue > 0 ? String(Number(amountDue.toFixed(2))) : "");
      setPaymentBank(null);
      setPaymentMemo("");
    }

    setPaymentOpen(true);
  };

  const savePayment = async () => {
    // Synchronous guard against two immediate invocations (e.g. a double
    // click or repeated tap before React re-renders the disabled button)
    // resulting in two POSTs -- `paying` state alone can't catch this
    // since it only updates on the next render. Mirrors
    // PurchaseDetail.tsx's handleAddPayment / CreditCardPayableDetail.tsx's
    // submitPayment. Independent of the attempt state machine below.
    if (fixedAssetPaymentInFlightRef.current) return;

    if (!asset?.id) return;

    setPaymentErr(null);

    const ymd = mdyToYmd(paymentDateMdy);
    if (!ymd) return setPaymentErr(localMessage("fixedAssetDetail.invalidDate"));

    const amt = Number(
      String(paymentAmount || "0")
        .replace(/,/g, "")
        .trim(),
    );
    if (!Number.isFinite(amt) || amt <= 0) {
      return setPaymentErr(localMessage("fixedAssetDetail.positivePayment"));
    }

    if (amt > amountDue) {
      return setPaymentErr(localMessage("fixedAssetDetail.paymentAboveDue"));
    }

    if (!paymentBank?.id) {
      return setPaymentErr(localMessage("fixedAssetDetail.selectPaymentAccount"));
    }

    // Validation passed -- only now do we build the canonical payload and
    // ask the state machine whether this is a first attempt (mint a new
    // key), an unchanged retry of a still-ambiguous SAME-asset attempt
    // (reuse its key), an edited retry of one (block, no network call),
    // or a retry against a DIFFERENT asset while an ambiguous attempt is
    // still outstanding (block, no network call, no silent key rotation).
    const assetId = Number(asset.id);
    const canonicalPayload = canonicalFixedAssetPayment(
      assetId,
      ymd,
      amt,
      paymentBank.id,
      paymentMemo || "",
    );
    const fingerprint = fingerprintFixedAssetPayment(canonicalPayload);
    const decision = decideFixedAssetPaymentAttempt(
      fixedAssetPaymentAttemptRef.current,
      assetId,
      canonicalPayload,
      fingerprint,
      randomUuidKey,
    );

    if (decision.kind === "blocked-mismatch") {
      // The previous attempt under this key may already have been
      // recorded -- never silently mint a new key or retry with edited
      // details under the same one.
      setPaymentErr(localMessage("fixedAssetDetail.editedRetry"));
      return;
    }
    if (decision.kind === "blocked-cross-asset") {
      setPaymentErr(localMessage("fixedAssetDetail.crossAsset"));
      return;
    }
    if (decision.kind === "blocked-in-flight") {
      // Unreachable in practice -- fixedAssetPaymentInFlightRef.current
      // already returns above before this point. Kept for correctness of
      // the pure decision function itself.
      return;
    }

    fixedAssetPaymentAttemptRef.current = decision.attempt;
    fixedAssetPaymentInFlightRef.current = true;

    setPaying(true);
    try {
      // Send the SAME canonicalPayload object used to build the
      // fingerprint above -- never a separately reconstructed copy --
      // plus idempotency_key. Mirrors PurchaseDetail.tsx's
      // handleAddPayment. asset_id is included here even though the
      // backend only reads it from the URL path (never from the body);
      // sending it is harmless and keeps this object identical to the one
      // fingerprinted.
      await apiPost(withCompany(`/fixed-assets/${asset.id}/payments`), {
        ...canonicalPayload,
        idempotency_key: decision.idempotencyKey,
      });

      // Confirmed success (create or replay -- both return the same
      // response shape): clear the attempt so the next payment (e.g. a
      // later distinct partial payment) gets a fresh key instead of
      // leaking this one into it.
      fixedAssetPaymentAttemptRef.current = resolveFixedAssetPaymentSuccess();

      setPaymentOpen(false);
      await refresh();
    } catch (e: any) {
      const failureClass = classifyFixedAssetPaymentFailure(e, isClosedPeriodError);

      if (failureClass === "closed-period") {
        // Definite pre-commit rejection: assert_year_open raises before
        // any claim/business write commits (same savepoint as every other
        // create()-time HTTPException), so nothing was persisted. Clear
        // the attempt so a corrected date can retry under a new key.
        fixedAssetPaymentAttemptRef.current = resolveFixedAssetPaymentDefiniteRejection();
        setPaymentErr(localMessage("sales.errors.closedSubmit"));
        return;
      }

      if (failureClass === "idempotency-conflict") {
        // Definite: this key is permanently bound to a different,
        // already-completed request. Retrying under it can never
        // succeed for this payload -- clear it so the next attempt mints
        // a fresh one.
        fixedAssetPaymentAttemptRef.current = resolveFixedAssetPaymentDefiniteRejection();
        setPaymentErr(localMessage("fixedAssetDetail.conflict"));
        return;
      }

      if (failureClass === "definite-rejection") {
        // HTTP 400/404 business or request validation failure -- nothing
        // persisted. Clear the attempt so the user can edit and retry
        // under a new key.
        fixedAssetPaymentAttemptRef.current = resolveFixedAssetPaymentDefiniteRejection();
        setPaymentErr(errorMessage(e, "sales.errors.recordPayment"));
        return;
      }

      // Ambiguous (network failure, timeout, unreadable response, HTTP
      // 500, or anything else unrecognized): retain the key, fingerprint,
      // and submitted payload so an unchanged retry reuses the same key
      // and safely replays instead of risking a duplicate; an edited
      // retry -- or a retry against a different asset -- stays blocked
      // above.
      fixedAssetPaymentAttemptRef.current = resolveFixedAssetPaymentAmbiguousFailure(
        fixedAssetPaymentAttemptRef.current,
      );
      setPaymentErr(errorMessage(e, "sales.errors.recordPayment"));
    } finally {
      fixedAssetPaymentInFlightRef.current = false;
      setPaying(false);
    }
  };

  const handleSaveMemo = async () => {
    if (!asset) return;

    setMemoError(null);
    setMemoSaving(true);

    const next = (memoDraft || "").trim();

    try {
      await apiPatch(withCompany(`/fixed-assets/${asset.id}`), {
        memo: next === "" ? null : next,
      });

      setMemo(next);
      setEditingMemo(false);
    } catch (e: any) {
      setMemoError(errorMessage(e, "depositDetail.memoError"));
    } finally {
      setMemoSaving(false);
    }
  };

  const openDelete = () => {
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!asset) return;

    if (!deleteAvailable) {
      setDeleteError(
        localMessage("fixedAssetDetail.deleteBlocked"),
      );
      return;
    }

    setDeleting(true);
    setDeleteError(null);
    try {
      await apiDelete(withCompany(`/fixed-assets/${asset.id}`));
      backToList();
    } catch (e: any) {
      if (isClosedPeriodError(e)) {
        setDeleteError(localMessage("sales.errors.closedEdit"));
        return;
      }

      setDeleteError(errorMessage(e, "fixedAssetDetail.deleteError"));
    } finally {
      setDeleting(false);
    }
  };

  if (!company_id) return null;

  return (
    <div
      className={`min-h-screen bg-gray-50 flex flex-col items-center pt-6 ${bodyCls}`}
    >
      {/* Header (sticky spacing tokens) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-6xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={backToList}
          className={iconBtnCls}
          aria-label={t("directory.common.back")}
          title={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none">
          <div className="text-[15px] font-semibold text-gray-700 text-center">
            {t("fixedAssetDetail.title")}
          </div>
        </div>

        <button
          type="button"
          onClick={backToList}
          className={iconBtnCls}
          aria-label={t("directory.common.close")}
          title={t("directory.common.close")}
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      <div className={`${cardCls} w-full max-w-3xl mx-auto px-6 py-5`}>
        {loading ? (
          <div className="text-xs text-gray-500">{t("directory.common.loading")}</div>
        ) : err ? (
          <div className="text-[13px] text-red-600">{displayMessage(err)}</div>
        ) : !asset ? (
          <div className="text-[13px] font-normal text-gray-700">
            {t("fixedAssetDetail.notFound")}
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className={`${cardCls} px-4 py-3 mb-4`}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3 mb-3">
                <div>
                  <div className="text-xs text-gray-500">
                    {t("fixedAssetDetail.depreciationStart")}
                  </div>
                  <div className="text-[13px] font-medium text-gray-700 tabular-nums">
                    {topStartYmd ? formatMMDDYYYYFromYMD(topStartYmd) : "—"}
                  </div>
                </div>

                <div className="col-span-2">
                  <div className="text-xs text-gray-500">{t("fixedAssetDetail.assetName")}</div>
                  <div className="text-[13px] font-medium text-gray-700 truncate">
                    {asset.name || "—"}
                  </div>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <div className="text-xs text-gray-500">{t("expenses.common.category")}</div>
                  <div className="text-[13px] text-gray-700 truncate">
                    {asset.asset_account_name || "—"}
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-gray-500">{t("sales.common.status")}</div>
                  <div className={statusPillClasses(statusLabel)}>
                    {displayAssetStatus(statusLabel)}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs text-gray-500">{t("directory.products.cost")}</div>
                  <div className="text-[13px] text-gray-700 tabular-nums">
                    {topCost == null ? "—" : money(topCost)}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs text-gray-500">{t("spendingFixedAssets.nbv")}</div>
                  <div className="text-[13px] text-gray-700 tabular-nums">
                    {topNbv == null ? "—" : money(topNbv)}
                  </div>
                </div>
              </div>
            </div>

            {showPaymentSection ? (
              <div className={`${cardCls} px-4 py-3 mb-4`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[15px] font-semibold text-gray-700">
                      {t("sales.common.payment")}
                    </div>
                    <div className="mt-1">
                      <StatusBubble
                        label={displayPaymentStatus(paymentBubble.label)}
                        bubbleType={paymentBubble.bubbleType}
                      />
                    </div>
                  </div>

                  {canRecordPayment ? (
                    <Button
                      variant="primary"
                      type="button"
                      onClick={openPayment}
                    >
                      {t("fixedAssetDetail.recordPayment")}
                    </Button>
                  ) : null}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3">
                  <div>
                    <div className="text-xs text-gray-500">{t("fixedAssetDetail.purchaseAmount")}</div>
                    <div className="text-[13px] text-gray-700 tabular-nums">
                      {purchaseAmount == null ? "—" : money(purchaseAmount)}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-gray-500">{t("sales.common.amountPaid")}</div>
                    <div className="text-[13px] text-gray-700 tabular-nums">
                      {money(amountPaid)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500">{t("payables.amountDue")}</div>
                    <div className="text-[13px] font-medium text-gray-700 tabular-nums">
                      {money(amountDue)}
                    </div>
                  </div>

                  {asset?.on_account ? (
                    <div className="text-right">
                      <div className="text-xs text-gray-500">{t("sales.common.dueDate")}</div>
                      <div className="text-[13px] text-gray-700 tabular-nums">
                        {asset?.due_date
                          ? formatMMDDYYYYFromYMD(asset.due_date)
                          : "—"}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* Depreciation History */}
            <div className={`${cardCls} px-4 py-3 mb-4`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[15px] font-semibold text-gray-700">
                    {t("fixedAssetDetail.history")}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-6 gap-y-1">
                    <div>
                      {t("fixedAssetDetail.startPrefix")}{" "}
                      <span className="text-gray-700 tabular-nums">
                        {topStartYmd ? formatMMDDYYYYFromYMD(topStartYmd) : "—"}
                      </span>
                    </div>
                    <div>
                      {t("spendingFixedAssets.endPrefix")}{" "}
                      <span className="text-gray-700 tabular-nums">
                        {topEndYmd ? formatMMDDYYYYFromYMD(topEndYmd) : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  className="flex items-center gap-2"
                  data-menu-root="fa-detail"
                >
                  <div className="relative">
                    <button
                      type="button"
                      className={[
                        iconBtnCls,
                        menuOpen ? "bg-gray-50" : "bg-white",
                      ].join(" ")}
                      onClick={() => setMenuOpen((v) => !v)}
                      aria-label={t("fixedAssetDetail.menu")}
                      title={t("fixedAssetDetail.menu")}
                      disabled={!schedSummary}
                    >
                      <EllipsisVerticalIcon className="w-5 h-5 text-gray-600" />
                    </button>

                    {menuOpen && (
                      <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden z-10">
                        <button
                          className="w-full text-left px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50"
                          onClick={() => {
                            setMenuOpen(false);
                            goEdit();
                          }}
                          type="button"
                        >
                          {t("directory.common.edit")}
                        </button>

                        <button
                          className="w-full text-left px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                          onClick={openDispose}
                          disabled={!schedSummary || !!asset.disposed}
                          type="button"
                        >
                          {t("fixedAssetDetail.dispose")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                {!schedSummary ? (
                  <div className="text-xs text-gray-500">
                    {t("fixedAssetDetail.noSchedule")}
                  </div>
                ) : !historySupported ? (
                  <div className="text-xs text-gray-500">
                    {t("fixedAssetDetail.historyUnavailable")}
                  </div>
                ) : postings.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    {t("fixedAssetDetail.noDepreciation")}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className={tableCls}>
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">
                              {t("sales.common.date")}
                            </th>
                            <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 tabular-nums">
                              {t("directory.products.cost")}
                            </th>
                            <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 tabular-nums">
                              {t("fixedAssetDetail.depreciation")}
                            </th>
                            <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 tabular-nums">
                              {t("spendingFixedAssets.nbv")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {postings.map((p) => (
                            <tr key={p.id} className="border-t border-gray-100">
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700 tabular-nums">
                                {formatMMDDYYYYFromYMD(p.period_end)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700 tabular-nums">
                                {money(p.cost)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700 tabular-nums font-medium">
                                {money(p.amount)}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700 tabular-nums">
                                {money(p.nbv)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-400 mt-3">
                  {t("fixedAssetEdit.prospectiveChanges")}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {t("fixedAssetDetail.recordAll")}
                </div>
              </div>
            </div>

            {/* Memo */}
            {!editingMemo ? (
              <MemoSection
                label={t("sales.common.memo")}
                editAriaLabel={t("sales.common.editMemo")}
                mode="view"
                variant="card"
                value={memo || ""}
                emptyText={t("purchases.detail.noMemo")}
                className="mb-6"
                onEditClick={() => {
                  setEditingMemo(true);
                  setMemoDraft(memo || "");
                  setMemoError(null);
                }}
              />
            ) : (
              <div className="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
                <MemoSection
                  mode="edit"
                  variant="plain"
                  label={t("sales.common.memo")}
                  value={memoDraft}
                  onChange={setMemoDraft}
                  rows={2}
                  disabled={memoSaving}
                  placeholder={t("sales.common.memoPlaceholder")}
                />

                <div className="flex gap-3 justify-end mt-3">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setEditingMemo(false);
                      setMemoDraft(memo || "");
                      setMemoError(null);
                    }}
                    disabled={memoSaving}
                  >
                    {t("directory.common.cancel")}
                  </Button>

                  <Button
                    variant="primary"
                    type="button"
                    onClick={handleSaveMemo}
                    disabled={memoSaving}
                  >
                    {memoSaving ? t("directory.common.savingEllipsis") : t("directory.common.save")}
                  </Button>
                </div>

                <div className="min-h-[18px] mt-2">
                  {memoError ? (
                    <div className="text-xs text-red-500 font-medium">
                      {displayMessage(memoError)}
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Delete */}
            <div className="flex justify-end">
              <Button variant="secondary" type="button" onClick={openDelete}>
                {t("directory.common.delete")}
              </Button>
            </div>

            {/* Delete modal */}
            <ConfirmDeleteModal
              open={deleteOpen}
              title={t("fixedAssetDetail.deleteTitle")}
              message={
                deleteAvailable
                  ? t("fixedAssetDetail.deleteHint")
                  : t("fixedAssetDetail.deleteBlocked")
              }
              deleting={deleting}
              error={displayMessage(deleteError)}
              onCancel={() => {
                setDeleteOpen(false);
                setDeleteError(null);
              }}
              onConfirm={confirmDelete}
              cardCls={cardCls}
              bodyCls={bodyCls}
            />
          </>
        )}
      </div>

      <ModalShell
        open={paymentOpen}
        title={t("fixedAssetDetail.recordPayment")}
        onClose={() => setPaymentOpen(false)}
        cardCls={cardCls}
        iconBtnCls={iconBtnCls}
        bodyCls={bodyCls}
      >
        <div className="space-y-4">
          {paymentErr ? (
            <div className="text-xs text-red-500 font-medium">{displayMessage(paymentErr)}</div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>{t("sales.common.date")}</label>
              <input
                className={inputCls}
                value={paymentDateMdy}
                onChange={(e) =>
                  setPaymentDateMdy(normalizeMDY(e.target.value))
                }
                placeholder="MM/DD/YYYY"
              />
            </div>

            <div>
              <label className={labelCls}>{t("sales.common.amount")}</label>
              <AppleNumberInput
                className={`${inputCls} text-right tabular-nums`}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                autoComplete="off"
              />
            </div>

            <div>
              <label className={labelCls}>{t("fixedAssetDetail.paidFrom")}</label>
              <AppleComboBox<Account>
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
                preserveQueryOnDisplayChange
                options={cashAccounts}
                value={paymentBank}
                onChange={setPaymentBank}
                getOptionLabel={paymentAccountLabel}
                getOptionSearchText={(a) => a?.name || ""}
                placeholder={t("fixedAssetDetail.selectCashBank")}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("sales.common.memo")}</label>
            <input
              className={inputCls}
              value={paymentMemo}
              onChange={(e) => setPaymentMemo(e.target.value)}
              placeholder={t("sales.detail.optional")}
            />
          </div>

          <div className="text-xs text-gray-500">
            {t("fixedAssetDetail.amountDuePrefix")}{" "}
            <span className="text-gray-700 tabular-nums">
              {money(amountDue)}
            </span>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setPaymentOpen(false)}
              disabled={paying}
            >
              {t("directory.common.cancel")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={savePayment}
              disabled={paying}
            >
              {paying ? t("directory.common.savingEllipsis") : t("fixedAssetDetail.savePayment")}
            </Button>
          </div>
        </div>
      </ModalShell>

      {/* Dispose modal */}
      <ModalShell
        open={disposeOpen}
        title={t("fixedAssetDetail.disposeTitle")}
        onClose={() => setDisposeOpen(false)}
        cardCls={cardCls}
        iconBtnCls={iconBtnCls}
        bodyCls={bodyCls}
      >
        <div className="space-y-4">
          {disposeErr ? (
            <div className="text-xs text-red-500 font-medium">{displayMessage(disposeErr)}</div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>{t("sales.common.date")}</label>
              <input
                className={inputCls}
                value={disposeDateMdy}
                onChange={(e) =>
                  setDisposeDateMdy(normalizeMDY(e.target.value))
                }
                placeholder="MM/DD/YYYY"
              />
            </div>

            <div>
              <label className={labelCls}>{t("fixedAssetDetail.proceeds")}</label>
              <AppleNumberInput
                className={`${inputCls} text-right tabular-nums`}
                value={disposeProceeds}
                onChange={(e) => setDisposeProceeds(e.target.value)}
                placeholder="0.00"
                autoComplete="off"
              />
            </div>

            <div>
              <label className={labelCls}>{t("fixedAssetDetail.depositTo")}</label>
              <AppleComboBox<Account>
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
                preserveQueryOnDisplayChange
                options={cashAccounts}
                value={disposeBank}
                onChange={setDisposeBank}
                getOptionLabel={(a) => a?.name || ""}
                placeholder={t("fixedAssetDetail.selectCashBank")}
                disabled={
                  Number(String(disposeProceeds || "0").replace(/,/g, "")) <= 0
                }
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("sales.common.memo")}</label>
            <input
              className={inputCls}
              value={disposeMemo}
              onChange={(e) => setDisposeMemo(e.target.value)}
              placeholder={t("sales.detail.optional")}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setDisposeOpen(false)}
              disabled={disposing}
            >
              {t("directory.common.cancel")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={saveDispose}
              disabled={disposing}
            >
              {disposing ? t("fixedAssetDetail.disposing") : t("fixedAssetDetail.dispose")}
            </Button>
          </div>
        </div>
      </ModalShell>
    </div>
  );
}
